import React from 'react';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb.tsx';
import { clsx } from 'clsx';
import { useTourBuilderContext } from '@/features/dashboard/tourConfig/contexts/TourBuilderContext.tsx';
import { useLocation, useNavigate, useParams } from '@tanstack/react-router';
import { CheckIcon } from 'lucide-react';
import { toast } from 'sonner';
import useGetTourPrices from '@/features/dashboard/tourConfig/hooks/useGetTourPrices.ts';
import useGetTransport from '@/features/dashboard/tourTransport/hooks/useGetTransport.ts';

interface Step {
  number: number;
  path: string;
  label: string;
  isCompleted: boolean;
}

const TourConfigBreadcrumbs = () => {
  const { tourId } = useParams({ strict: false });
  const navigate = useNavigate();
  const location = useLocation();

  const { state, updateTourBuilderState } = useTourBuilderContext();

  const { data: tourPrices } = useGetTourPrices(tourId);
  const { data: transport } = useGetTransport(tourId);

  const handleNavigation = (step: Step) => {
    if (tourId === undefined) {
      toast(
        'Podaj informację o wyjeździe',
        // ToastType.WARNING,
        // 'Aby przejść do następnego kroku, musisz najpierw podać informacje o wyjeździe.',
      );
    } else {
      navigate({ to: `/app/tours/${tourId}${step.path}` });
      //updateTourBuilderState({ currentStep: step.number });
    }
  };

  const steps: Step[] = [
    {
      number: 1,
      path: '/config/basics',
      label: 'Informacje',
      isCompleted: tourId !== undefined,
    },
    {
      number: 2,
      path: '/config/costs',
      label: 'Koszty',
      isCompleted: tourPrices !== undefined,
    },
    {
      number: 3,
      path: '/config/transport',
      label: 'Transport',
      isCompleted: transport != null,
    },
    {
      number: 4,
      path: '/config/options',
      label: 'Opcje dodatkowe',
      isCompleted: false,
    },
    {
      number: 5,
      path: '/config/installments',
      label: 'Terminy i metody płatności',
      isCompleted: false,
    },
    {
      number: 6,
      path: '/config/form',
      label: 'Formularz rejestracyjny',
      isCompleted: false,
    },
    {
      number: 7,
      path: '/config/consents',
      label: 'Zgody',
      isCompleted: false,
    },
    {
      number: 8,
      path: '/config/insurances',
      label: 'Ubezpieczenie',
      isCompleted: false,
    },
  ];

  const currentStep =
    steps.find((s) => location.pathname.endsWith(s.path))?.number || 1;

  return (
    <Breadcrumb className="border-b border-neutral-100 pb-3">
      <BreadcrumbList>
        {steps.map((step, index) => (
          <React.Fragment key={step.number}>
            {index > 0 && <BreadcrumbSeparator />}
            <button
              onClick={() => handleNavigation(step)}
              className={clsx(
                'flex cursor-pointer flex-row items-center gap-1 border-b-2 px-1 pb-1 pt-0.5 transition hover:opacity-90',
                {
                  'border-b-2 border-accent-600': currentStep === step.number,
                },
              )}
            >
              <div
                className={clsx(
                  'flex h-4 w-4 items-center justify-center rounded-lg',
                  {
                    'bg-accent-500': currentStep === step.number,
                    'bg-neutral-200': currentStep < step.number,
                    'bg-success-200': currentStep > step.number,
                  },
                )}
              >
                {step.isCompleted && currentStep !== step.number ? (
                  <CheckIcon size={12} />
                ) : (
                  <div
                    className={clsx('text-[10px]', {
                      'text-accent-800': currentStep === step.number,
                      'text-neutral-500': currentStep < step.number,
                    })}
                  >
                    {step.number}
                  </div>
                )}
              </div>
              <div
                className={clsx('text-sm font-medium', {
                  'text-basic-black': currentStep === step.number,
                  'text-neutral-600': currentStep !== step.number,
                })}
              >
                {step.label}
              </div>
            </button>
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export default TourConfigBreadcrumbs;
